import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { useNavigate, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, X, CheckCircle2, Package, AlertCircle, Keyboard } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const READER_ID = "qr-reader-region";

export default function Scanner({ defaultAction }) {
  // defaultAction is "packed" (staff) or "delivered" (partner). Admins can switch.
  const { user } = useAuth();
  const ctx = useOutletContext();
  const brand = ctx?.brand;
  const nav = useNavigate();

  const role = user?.role || "customer";
  const initialAction = defaultAction || (role === "customer" ? "delivered" : "packed");

  const [action, setAction] = useState(initialAction);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState("");
  const scannerRef = useRef(null);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch { /* ignore */ }
      try { await scannerRef.current.clear(); } catch { /* ignore */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => () => { stopScanner(); }, []); // eslint-disable-line

  const startScanner = async () => {
    setResult(null);
    setScanning(true);
    await new Promise((r) => setTimeout(r, 50)); // wait for DOM mount
    try {
      const inst = new Html5Qrcode(READER_ID);
      scannerRef.current = inst;
      await inst.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decoded) => { await handlePayload(decoded); },
        () => { /* per-frame errors ignored */ },
      );
    } catch (e) {
      toast.error("Could not start camera. Try the manual entry below.");
      setScanning(false);
    }
  };

  const handlePayload = async (payload) => {
    await stopScanner();
    try {
      const { data } = await api.post("/orders/scan", { payload: payload.trim(), action });
      setResult({ ok: true, data });
      const status = data.order?.status;
      if (data.kind === "product") {
        toast.success(`Product: ${data.product.name}`);
      } else if (data.applied) {
        toast.success(`Order ${data.order?.order_no} → ${status}`);
      } else {
        toast.info(data.message || "No change");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Scan failed";
      setResult({ ok: false, error: detail });
      toast.error(detail);
    }
  };

  const submitManual = () => {
    if (!manual.trim()) return toast.error("Enter a code");
    handlePayload(manual);
  };

  const brandPrimary = brand?.primary || "#C85A32";

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <p className="overline" style={{ color: brand?.subtle || "#8C8581" }}>
        {brand?.name || (role === "staff" ? "Staff" : "Admin")} • Scan
      </p>
      <h1 className="font-heading font-bold text-3xl mt-2 mb-6">
        Scan {action === "packed" ? "to mark packed" : "to confirm delivery"}
      </h1>

      {(role === "admin" || role === "staff") && (
        <div className="mb-6 inline-flex rounded-full border border-[#2D2622]/15 p-1 text-sm">
          <button data-testid="scan-mode-packed" onClick={() => setAction("packed")}
            className={`px-4 py-1.5 rounded-full font-semibold ${action === "packed" ? "text-white" : "text-[#5C5753]"}`}
            style={{ background: action === "packed" ? brandPrimary : "transparent" }}>Mark packed</button>
          <button data-testid="scan-mode-delivered" onClick={() => setAction("delivered")}
            className={`px-4 py-1.5 rounded-full font-semibold ${action === "delivered" ? "text-white" : "text-[#5C5753]"}`}
            style={{ background: action === "delivered" ? brandPrimary : "transparent" }}>Mark delivered</button>
        </div>
      )}

      <div className="rounded-2xl border-2 border-dashed border-[#2D2622]/15 bg-[#FAF8F3] p-6 text-center">
        {!scanning && !result && (
          <div className="py-8">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: brandPrimary }}>
              <Camera className="w-7 h-7 text-white" />
            </div>
            <p className="text-[#5C5753] mb-4">Point your camera at the dispatch slip QR or the product QR.</p>
            <Button data-testid="start-scan-btn" onClick={startScanner} className="text-white" style={{ background: brandPrimary }}>
              <Camera className="w-4 h-4 mr-1.5" /> Start camera
            </Button>
          </div>
        )}

        {scanning && (
          <div>
            <div id={READER_ID} data-testid="qr-reader-region" className="mx-auto" style={{ maxWidth: 360 }} />
            <Button variant="outline" onClick={stopScanner} className="mt-4">
              <X className="w-4 h-4 mr-1.5" /> Stop
            </Button>
          </div>
        )}

        {result && (
          <div className="text-left py-3">
            {result.ok ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: "#3D6B42" }} />
                <div className="flex-1">
                  {result.data.kind === "product" ? (
                    <>
                      <p className="font-semibold">Product · {result.data.product.name}</p>
                      <p className="text-sm text-[#5C5753]">{result.data.product.category} • ₹{result.data.product.price} / {result.data.product.unit} • Stock: {result.data.product.stock}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold">Order {result.data.order?.order_no}</p>
                      <p className="text-sm text-[#5C5753]">
                        Status: <span className="font-semibold" style={{ color: brandPrimary }}>{result.data.order?.status}</span>
                        {" · "}{result.data.order?.store_name} ({result.data.order?.store_code})
                      </p>
                      {!result.data.applied && <p className="text-xs text-[#8C8581] mt-1">{result.data.message}</p>}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: "#A6291E" }} />
                <p className="font-semibold text-[#A6291E]" data-testid="scan-error">{result.error}</p>
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <Button data-testid="scan-again-btn" onClick={() => { setResult(null); startScanner(); }} className="text-white" style={{ background: brandPrimary }}>
                <Camera className="w-4 h-4 mr-1.5" /> Scan another
              </Button>
              {result.data?.order?.id && (
                <Button variant="outline" onClick={() => nav(brand ? `/b/${brand.key}/orders/${result.data.order.id}` : `/admin`)}>
                  <Package className="w-4 h-4 mr-1.5" /> Open order
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-[#2D2622]/10 p-4 bg-white">
        <div className="flex items-center gap-2 mb-2">
          <Keyboard className="w-4 h-4 text-[#8C8581]" />
          <p className="text-sm font-semibold text-[#2D2622]">Manual entry</p>
        </div>
        <p className="text-xs text-[#8C8581] mb-2">If the camera fails, paste the QR contents below (e.g. <span className="font-mono">ORD:abc123</span>).</p>
        <div className="flex gap-2">
          <Input data-testid="scan-manual-input" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="ORD:... or LINE:... or PRD:..." />
          <Button data-testid="scan-manual-submit" onClick={submitManual}>Submit</Button>
        </div>
      </div>
    </div>
  );
}
